const DEFAULT_MAX_CONCURRENT_LOADS = 2
const MAX_CONCURRENT_LOADS = 8

interface ParsedMeshResult {
  readonly glbs: readonly ResolvedGlbReference[]
}

interface ResolvedGlbReference {
  readonly reference: string
  readonly url: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function resolveGlbReference(value: unknown, index: number, baseUrl: string): ResolvedGlbReference {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`data.glb_model_list[${index}] 必须是非空字符串`)
  }
  const normalized = value.trim().replace(/\\/g, '/')
  const pathSegments = normalized.split(/[?#]/, 1)[0].split('/').filter(Boolean)
  let decodedSegments: string[]
  try {
    decodedSegments = pathSegments.map((segment) => decodeURIComponent(segment))
  } catch {
    throw new Error(`data.glb_model_list[${index}] 包含无效的 URL 编码`)
  }
  if (
    normalized.startsWith('/') ||
    normalized.startsWith('//') ||
    /^[a-z][a-z\d+.-]*:/i.test(normalized) ||
    decodedSegments.some(
      (segment) => segment === '..' || segment.includes('/') || segment.includes('\\')
    )
  ) {
    throw new Error(`data.glb_model_list[${index}] 必须是清单目录内的相对路径`)
  }
  const resolvedBaseUrl = new URL(baseUrl, globalThis.location?.href ?? 'http://localhost/')
  const baseDirectoryUrl = new URL('.', resolvedBaseUrl)
  const url = new URL(normalized, resolvedBaseUrl)
  if (
    url.origin !== baseDirectoryUrl.origin ||
    !url.pathname.startsWith(baseDirectoryUrl.pathname)
  ) {
    throw new Error(`data.glb_model_list[${index}] 必须是清单目录内的相对路径`)
  }
  if (!url.pathname.toLowerCase().endsWith('.glb')) {
    throw new Error(`data.glb_model_list[${index}] 不是 .glb 文件`)
  }
  return {
    reference: pathSegments.filter((segment) => segment !== '.').join('/'),
    url: url.href,
  }
}

export function parseMeshResultManifest(
  data: ArrayBuffer | string,
  baseUrl: string
): ParsedMeshResult {
  const text =
    typeof data === 'string' ? data.replace(/^\uFEFF/, '') : new TextDecoder().decode(data)
  const manifest: unknown = JSON.parse(text)
  if (!isRecord(manifest)) throw new Error('mesh_result.json 根节点必须是对象')
  if (manifest.code !== 0 || manifest.state !== 'succeeded') {
    throw new Error(
      `mesh_result.json 任务未成功，code: ${String(manifest.code)}，state: ${String(
        manifest.state
      )}`
    )
  }
  if (!isRecord(manifest.data) || !Array.isArray(manifest.data.glb_model_list)) {
    throw new Error('mesh_result.json 缺少 data.glb_model_list')
  }
  if (manifest.data.glb_model_list.length === 0) {
    throw new Error('mesh_result.json 的 data.glb_model_list 不能为空')
  }
  const uniqueByUrl = new Map<string, ResolvedGlbReference>()
  for (const [index, value] of manifest.data.glb_model_list.entries()) {
    const resolved = resolveGlbReference(value, index, baseUrl)
    if (!uniqueByUrl.has(resolved.url)) uniqueByUrl.set(resolved.url, resolved)
  }
  return { glbs: [...uniqueByUrl.values()] }
}

export function normalizeMaxConcurrentLoads(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_MAX_CONCURRENT_LOADS
  return Math.min(MAX_CONCURRENT_LOADS, Math.max(1, Math.floor(value)))
}
